import React, { useState, useEffect, useRef, useMemo } from "react";
import { Form } from "react-bootstrap";
import WDPPanel from "./WDPPanel";
import FluentdHelper from "../../helpers/FluentdHelper";
import {
  BLACK,
  CLAIMS,
  CROPS,
  EMPTY,
  FIELDS,
  LOGERROR,
  MAP_CENTER,
  RESOURCES,
  SESSION_EXPIRED,
  SESSION_EXPIRED_MESSAGE,
  SHARED_FIELDS,
  USER_DETAILS,
} from "../../configurations/Constants";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Tooltip,
  CircleMarker,
} from "react-leaflet";
import GisLayerService from "../../api-service/GisLayerService";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import L, { LatLngBounds } from "leaflet";
import {
  ERROR_FETCHING_ALL_IRRIGATION_FIELDS,
  ERROR_FETCHING_FIELD_DETAILS,
  ERROR_FETCHING_PRECIP_DATA,
  INCOMING_TOKEN_HAS_EXPIRED,
  INVALID_CREDENTIALS,
  SESSION_HAS_EXPIRED,
} from "../../configurations/MessageConstants";
import { ADDED, SHAREDFIELD } from "../../configurations/Constants";
import UserService from "../../api-service/UserService";
import SessionHelper from "../../helpers/SessionHelper";
import GoogleMapHelper from "../../helpers/GoogleMapHelper";
import {
  SetFlagToGetGeoJson,
  setAllGeoJsonPolygon,
  setUserCropDetails,
  allUserRegisteredFields,
  setInitialFarms,
  setInitialBusiness,
} from "../../configurations/redux-configurations/actions/fieldAction";
import StorageHelper from "../../helpers/StorageHelper";
import LoadingOverlay from "../common/LoadingOverlay";
import PrecipFilters from "./PrecipFilters";
import PrecipService from "../../api-service/PrecipService";
import { ToastContainer, toast } from "react-toastify";
import MarkerClusterGroup from "react-leaflet-cluster";
import ChangeMap from "../common/ChangeMap";
import { StatusCodes } from "../../configurations/status-codes";

export function WDPMap() {
  const dispatch = useDispatch();
  const AllGeoJson = useSelector((state) => state.field.allPolygons);
  const IsGeoJsonFetched = useSelector(
    (state) => state.field.isGeoJsonPolygonFetched
  );
  const loggedInUser = useSelector((state) => state.user.loggedInUser);

  const [overlayLoading, setOverlayLoading] = useState(false);
  const [overlayLoadingText, setOverlayLoadingText] = useState(EMPTY);
  const [fieldsFiltered, setFieldsFiltered] = useState(false);
  const [userFieldDetails, setUserFieldDetails] = useState({});
  const [filteredList, setFilteredList] = useState([]);
  const [fieldClicked, setFieldClicked] = useState(false);
  const [selectedField, setSelectedField] = useState(EMPTY);
  const [expand, setExpand] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [yearAcresDetails, setYearAcresDetails] = useState({});
  const [doReload, setDoReload] = useState(false);
  const [allDisplayFields, setAllDisplayFields] = useState([]);
  const [allUserFields, setAllUserFields] = useState(null);
  const [allSharedFields, setAllSharedFields] = useState(null);
  const [latLongValue, setLatLongValue] = useState(null);
  const [maxZoom, setMaxZoom] = useState(12);
  const [wellMarkerArray, setWellMarkerArray] = useState([]);
  //Precip Map States
  const [precipFilterOn, setPrecipFilterOn] = useState(false);
  const [startDateISO, setStartDateISO] = useState(EMPTY);
  const [endDateISO, setEndDateISO] = useState(EMPTY);
  const [precipData, setPrecipData] = useState([]);
  const [streetView, setStreetView] = useState(false);
  const [showWdpPanel, setShowWdpPanel] = useState(false);
  const [hideLayers, setHideLayers] = useState(false);

  useEffect(() => {
    (async function () {
      setOverlayLoadingText("Loading Map...");
      setOverlayLoading(true);
      const allIrrigationFields = await loadAllIrrigationFields();
      let userFieldDetailsValue = await getUserFieldDetails();
      let allShared = userFieldDetailsValue.data.shared;
      let nonRejectedSharedfields = allShared.filter((field) => {
        return field.rejected === false;
      });
      userFieldDetailsValue.data.shared = nonRejectedSharedfields;
      let allSharedFieldsValue = _.map(
        nonRejectedSharedfields,
        "irrig_unit_id"
      );
      let allUserFieldsValue = _.map(
        userFieldDetailsValue.data.fields,
        "irrig_unit_id"
      );
      let combinedFields = userFieldDetailsValue.data.fields.concat(
        nonRejectedSharedfields
      );

      setFarmsAndGrowers(userFieldDetailsValue.data.resources);
      let yearAcresDetailsValue = getAcresYearData(userFieldDetailsValue.data);
      setOverlayLoading(false);
      setUserFieldDetails(userFieldDetailsValue);
      setDataFetched(true);
      setYearAcresDetails(yearAcresDetailsValue);
      setAllUserFields(allUserFieldsValue);
      setAllSharedFields(allSharedFieldsValue);
      displayMainPolygon(
        allSharedFieldsValue.concat(allUserFieldsValue),
        allIrrigationFields
      );
      setDoReload(true);

      let firstField = combinedFields[0];
      if (firstField) {
        let geomObject = getGeometry(firstField);
        if (geomObject) {
          firstField["geometry"] = geomObject.geometry;
        }
        setMapLocation(true, [firstField]);
      }
    })();
  }, []);

  useEffect(() => {
    if (precipFilterOn && startDateISO && endDateISO) {
      FetchPrecipitationData();
    }
  }, [precipFilterOn, startDateISO, endDateISO]);

  const getGeometry = (usersFields) => {
    if (AllGeoJson.hasOwnProperty("features")) {
      const geometry = _.find(AllGeoJson.features, (field) => {
        return field.properties.irrig_unit_id === usersFields.irrig_unit_id;
      });
      return geometry;
    } else {
      return null;
    }
  };

  const loadAllIrrigationFields = async () => {
    try {
      if (!IsGeoJsonFetched) {
        const allGeoJson = await GisLayerService.loadAllIrrigationFields();
        dispatch(setAllGeoJsonPolygon(allGeoJson));
        dispatch(SetFlagToGetGeoJson(true));
        if (allGeoJson.features.length === 0) return false;
        return allGeoJson;
      }
      const allGeoJson = AllGeoJson;
      if (allGeoJson.features === undefined || allGeoJson.features.length === 0)
        return false;

      return allGeoJson;
    } catch (error) {
      console.error(ERROR_FETCHING_ALL_IRRIGATION_FIELDS, error);
      return false;
    }
  };

  const displayMainPolygon = (userFields, allIrrigationFields) => {
    const allDisplayFieldsValue = [];
    _.forEach(allIrrigationFields.features, (field) => {
      if (_.includes(userFields, field.properties.irrig_unit_id)) {
        allDisplayFieldsValue.push(field);
      }
    });
    setAllDisplayFields(allDisplayFieldsValue);
  };
  const panelRef = useRef(null);

  const onEachPolygon = (polygon, layer) => {
    const layer_irrig_unit_id = layer.feature.properties
      ? layer.feature.properties.irrig_unit_id
      : layer.feature.irrig_unit_id;
    layer.setStyle({ className: layer_irrig_unit_id });

    const polygon_irrig_unit_id = polygon.properties
      ? polygon.properties.irrig_unit_id
      : polygon.irrig_unit_id;
    const userFieldIds =
      userFieldDetails?.data?.fields?.map((field) => field.irrig_unit_id) ??
      allUserFields ??
      [];
    const sharedFieldIds =
      userFieldDetails?.data?.shared?.map((field) => field.irrig_unit_id) ??
      allSharedFields ??
      [];
    if (_.includes(userFieldIds, polygon_irrig_unit_id)) {
      layer.setStyle({
        fillColor: ADDED, //orange
        color: hideLayers ? ADDED : BLACK,
        weight: hideLayers ? 2 : 1,
        fillOpacity: hideLayers ? 0 : 0.8,
      });
    }
    if (_.includes(sharedFieldIds, polygon_irrig_unit_id)) {
      layer.setStyle({
        fillColor: SHAREDFIELD, //purple
        color: hideLayers ? SHAREDFIELD : BLACK,
        weight: hideLayers ? 2 : 1,
        fillOpacity: hideLayers ? 0 : 0.8,
      });
    }

    layer.bindTooltip(getFieldName(polygon_irrig_unit_id), {
      permanent: true,
      direction: "left",
      className: "fieldlabel d-none",
    });

    layer.on({
      mouseover: (event) => {
        layer.setStyle({ weight: 3 });
      },
      mouseout: (event) => {
        layer.setStyle({ weight: hideLayers ? 2 : 1 });
      },
      click: (event) => {
        setLatLongValue(null);
        fieldClickEvent(layer_irrig_unit_id, true);
        if (panelRef.current.classList.contains("hide-sidebar")) {
          setShowWdpPanel(true);
          setTimeout(() => {
            highlightPanelCard(layer_irrig_unit_id);
          }, 800);
        } else {
          highlightPanelCard(layer_irrig_unit_id);
        }
      },
    });
  };

  const highlightPanelCard = (irrig_unit_id) => {
    let element = document.getElementById(irrig_unit_id);
    if (!element) return;
    element.scrollIntoView();
    let top = element.offsetTop - 100;
    element.scrollTop = top;
    let cardElement = document.getElementById("card_" + irrig_unit_id);
    cardElement.className += " highlight-effect";
    setTimeout(() => {
      cardElement.classList.remove("highlight-effect");
    }, 1000);
  };

  const getFieldName = (irrig_unit_id) => {
    const userFieldDetailsValue = userFieldDetails.data.fields;
    const userSharedDetailsValue = userFieldDetails.data.shared;
    const allFieldsDetails = userFieldDetailsValue.concat(
      userSharedDetailsValue
    );
    if (allFieldsDetails && Object.keys(allFieldsDetails).length > 0) {
      const field = allFieldsDetails.filter((item) => {
        return item.irrig_unit_id === irrig_unit_id;
      });
      if (field) {
        return field[0].field_name;
      }
    }
    return EMPTY;
  };

  const getAcresYearData = (userFieldDetails) => {
    let acresYearData = {};
    if (
      userFieldDetails.field_acres_year_wise &&
      Object.keys(userFieldDetails.field_acres_year_wise).length > 0
    ) {
      acresYearData = userFieldDetails.field_acres_year_wise;
    }
    return acresYearData;
  };

  const setMapLocation = (filter, fields) => {
    if (filter === false) {
      fields = userFieldDetails.data.fields;
    }
    if (fields.length > 0) {
      let bounds = new LatLngBounds();
      let center = GoogleMapHelper.getMapCenter([fields[0]], bounds);
      let latlng = _.values(center)[0];
      setMaxZoom(10);
      setLatLongValue(latlng);
    }
  };

  const setFarmsAndGrowers = async (userResources) => {
    const farms = [];
    let businesses = [];
    if (!userResources) return;
    for (let i = 0; i < (userResources.farms_and_businesses || []).length; i++) {
      farms.push({
        farmName: userResources.farms_and_businesses[i].farm_name,
        businessName: userResources.farms_and_businesses[i].business_name,
      });
    }
    businesses = userResources.businesses;
    dispatch(setInitialFarms(farms));
    dispatch(setInitialBusiness(businesses));
  };

  const getUserFieldDetails = async () => {
    const payload = {
      user_email: loggedInUser,
      keys: [FIELDS, CROPS, CLAIMS, RESOURCES, SHARED_FIELDS, USER_DETAILS],
    };
    try {
      const response = await UserService.getUserFieldDetails(payload);
      const userRegisteredFieldsValue = response.data;
      dispatch(allUserRegisteredFields(userRegisteredFieldsValue));
      dispatch(setUserCropDetails(userRegisteredFieldsValue.data.crops));
      return userRegisteredFieldsValue;
    } catch (error) {
      const responseData = error.response.data;
      console.error(ERROR_FETCHING_FIELD_DETAILS, error);
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        FluentdHelper.logEvent(
          INVALID_CREDENTIALS,
          "RegistrationMap_getUserFieldDetails",
          loggedInUser,
          {
            request: payload,
            response: responseData,
          },
          LOGERROR
        );
        StorageHelper.setItem(SESSION_EXPIRED, true);
        StorageHelper.setItem(SESSION_EXPIRED_MESSAGE, SESSION_HAS_EXPIRED);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };

  const showFieldOnMAP = async (
    value,
    paths,
    irrig_unit_ID,
    fillColor1,
    fillColor2
  ) => {
    let multi = 1000;
    let layer = document.getElementsByClassName(irrig_unit_ID)[0];
    setMaxZoom(15);
    setLatLongValue(value);
    for (let i = 0; i < 10; i++) {
      setTimeout(() => {
        layer.setAttribute("fill", i % 2 === 0 ? fillColor2 : fillColor1);
      }, multi);
      multi += 500;
    }
  };

  const filterFieldsOnMap = (list, geometries, sharedFields) => {
    if (sharedFields === false) {
      setFieldsFiltered(true);
      setFilteredList(list);
    } else {
      let userFieldDetailsValue = userFieldDetails;
      userFieldDetailsValue.data.shared = sharedFields;
      setFieldsFiltered(true);
      setFilteredList(list);
      setUserFieldDetails(userFieldDetailsValue);
    }
    setMapLocation(true, geometries);
  };

  const fieldClickEvent = (irrig_unit_id, clicked) => {
    setFieldClicked(clicked);
    setSelectedField(irrig_unit_id);
    setExpand(false);
  };

  const expandAll = () => {
    setFieldClicked(false);
    setExpand(true);
    setMaxZoom(null);
    setLatLongValue(null);
  };

  const collapseAll = () => {
    setFieldClicked(false);
    setExpand(false);
    setMaxZoom(null);
    setLatLongValue(null);
  };
  const removeWells = () => {
    if (wellMarkerArray && wellMarkerArray.length > 0) {
      setWellMarkerArray([]);
    }
  };
  const fieldCheckEvent = async (allCheckedFields, clicked) => {
    displayWell(allCheckedFields, clicked);
  };
  const fieldEditEvent = async (fieldData, clicked) => {
    displayWell([fieldData], clicked);
  };

  const displayWell = (fields, clicked) => {
    let markerArr = wellMarkerArray;
    if (markerArr && markerArr.length > 0) {
      markerArr = [];
    }
    fields.forEach((field) => {
      setFieldClicked(false);
      setSelectedField(field.irrig_unit_id);
      setExpand(false);
      let wellProps = [];
      if (field.fieldData) wellProps = field.fieldData.well_data;
      else wellProps = field.well_data;
      let wellCoord = [];
      let registeredWells = [];

      for (let key in wellProps) {
        wellCoord.push({
          coordinates: wellProps[key].coordinates,
          well_id: wellProps[key].well_id,
          pumpRate: wellProps[key].pump_rate,
        });

        registeredWells.push({
          id: wellProps[key].id,
          well_id: wellProps[key].well_id,
          gpm: wellProps[key].pump_rate,
          ugpm: wellProps[key].pump_rate,
          activeWell: true,
          isElectricWell: true,
        });
      }

      if (wellCoord && wellCoord.length > 0) {
        wellCoord.forEach((well, index) => {
          let wellCords = JSON.parse(well.coordinates);
          let wellMarker = {
            position: {
              lat: wellCords[1],
              lng: wellCords[0],
            },
            markerIcon: L.icon({
              iconUrl: require("../../images/pins/" + index + ".png"),
              iconSize: [15, 24],
              iconAnchor: [-1, 23],
              popupAnchor: [-3, -76],
            }),
          };
          markerArr.push(wellMarker);
          markerArr = markerArr.filter((v, i, a) => a.indexOf(v) === i);
        });
      }
    });
    setWellMarkerArray(markerArr);
  };

  let fields =
    Object.keys(userFieldDetails).length &&
    userFieldDetails.hasOwnProperty("data")
      ? userFieldDetails.data.fields
      : [];
  if (fieldsFiltered === true) {
    fields = fields.filter((field) => {
      return filteredList.includes(field.irrig_unit_id);
    });
  }
  let sharedFields =
    Object.keys(userFieldDetails).length &&
    userFieldDetails.hasOwnProperty("data")
      ? userFieldDetails.data.shared
      : [];

  if (fieldsFiltered === true) {
    sharedFields = sharedFields.filter((field) => {
      return filteredList.includes(field.irrig_unit_id);
    });
  }

  _.forEach(fields.concat(sharedFields), (field) => {
    field.type = "Feature";
  });
  let filterFields = fields.concat(sharedFields);

  const filteredDisplayFeatures = useMemo(() => {
    if (
      fieldsFiltered !== true ||
      !AllGeoJson?.features ||
      filteredList.length === 0
    ) {
      return null;
    }
    const filteredSet = new Set(filteredList);
    return AllGeoJson.features.filter((feature) =>
      filteredSet.has(feature.properties?.irrig_unit_id)
    );
  }, [fieldsFiltered, AllGeoJson?.features, filteredList]);

  const userFieldsWithCrop = {
    fields: userFieldDetails.data ? userFieldDetails.data.fields : [],
    crops: userFieldDetails.data ? userFieldDetails.data.crops : [],
  };

  const FetchPrecipitationData = async () => {
    try {
      setOverlayLoadingText("Loading Precipitation Data...");
      setOverlayLoading(true);
      const response = await PrecipService.getPrecipData(
        startDateISO,
        endDateISO
      );
      if (!response.data.data.precipData) {
        toast.warn(
          <div>
            Precipitation Sync Error
            <br />
            Please try again later
          </div>
        );
      }
      setPrecipData(response.data.data.precipData);
      setOverlayLoading(false);
    } catch (error) {
      setOverlayLoading(false);
      const responseData = error.response.data;
      console.error(ERROR_FETCHING_PRECIP_DATA, error);
      toast.warn(<div>{responseData.message}</div>);
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(SESSION_EXPIRED, true);
        StorageHelper.setItem(SESSION_EXPIRED_MESSAGE, SESSION_HAS_EXPIRED);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };
  const createClusterCustomIcon = (cluster) => {
    return new L.DivIcon({
      html: `<span>${cluster.getChildCount()}</span>`,
      className: "custom-marker-cluster",
      iconSize: L.point(33, 33, true),
    });
  };

  return (
    <div className="wdp-map">
      <LoadingOverlay
        showSpinner={true}
        active={overlayLoading}
        text={overlayLoadingText}
      />
      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <legend className="change-map-view">
        <div className="change-map-view-toggle">
          <span>Street Map</span>
          <Form.Check
            type="switch"
            checked={streetView}
            onChange={() => setStreetView(!streetView)}
            className="mt-2"
          />
        </div>
        <div className="d-flex align-items-center mt-1">
          <div className="d-block">
            <ul className="text-end p-0 m-1">
              <li className="precip-legend">Precipitation</li>
              <li className="registered ">Registered Fields</li>
              <li className="shared shared-text">Shared Fields</li>
            </ul>
          </div>
          <Form.Check
            type="checkbox"
            checked={hideLayers}
            onChange={() => setHideLayers(!hideLayers)}
            label="Hide Layers"
            className="hide-layers-checkbox"
          />
        </div>
      </legend>
      {!doReload ? (
        <React.Fragment></React.Fragment>
      ) : (
        <MapContainer
          className="farmer-map"
          style={{ width: "100%" }}
          center={MAP_CENTER}
          zoom={maxZoom}
          scrollWheelZoom={true}
          attributionControl={false}
        >
          <ChangeMap latLongValue={latLongValue} maxZoom={maxZoom} />
          <GeoJSON
            key={`${JSON.stringify(filterFields)}-${hideLayers}`}
            data={
              fieldsFiltered !== true
                ? allDisplayFields
                : filteredDisplayFeatures ?? filterFields
            }
            onEachFeature={onEachPolygon}
          ></GeoJSON>
          {streetView ? (
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          ) : (
            <TileLayer url="http://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}" />
          )}

          {wellMarkerArray
            ? wellMarkerArray.map((marker, idx) => (
                <Marker
                  key={idx}
                  position={[marker.position.lat, marker.position.lng]}
                  icon={marker.markerIcon}
                />
              ))
            : EMPTY}
          <PrecipFilters
            precipFilterOn={precipFilterOn}
            setPrecipFilterOn={setPrecipFilterOn}
            setStartDateISO={setStartDateISO}
            setEndDateISO={setEndDateISO}
            setPrecipData={setPrecipData}
            streetView={streetView}
            showWdpPanel={showWdpPanel}
          />
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={createClusterCustomIcon}
            polygonOptions={{
              fillColor: "#2196f3",
              color: "#003e58",
              weight: 2,
              opacity: 0.7,
              fillOpacity: 0.5,
            }}
          >
            {precipData?.map((location, index) => (
              <CircleMarker
                key={index}
                center={location.gps.reverse()}
                pathOptions={{ color: "transparent" }}
                radius={0}
              >
                <Tooltip
                  direction="top"
                  permanent={true}
                  className="precip-tooltip"
                >
                  {location.precip_total?.toFixed(2)}"
                </Tooltip>
              </CircleMarker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      )}
      {dataFetched === true && (
        <WDPPanel
          userRegisteredFields={{
            data: userFieldsWithCrop,
          }}
          userSharedField={sharedFields}
          userClaimedFields={
            userFieldDetails.data ? userFieldDetails.data.claims : []
          }
          showFieldOnMAP={(
            coords,
            paths,
            irrig_unit_ID,
            fillColor1,
            fillColor2
          ) =>
            showFieldOnMAP(coords, paths, irrig_unit_ID, fillColor1, fillColor2)
          }
          filterFieldsOnMap={(list, geometries, sharedFields) =>
            filterFieldsOnMap(list, geometries, sharedFields)
          }
          field_check={(layer, clicked) => fieldCheckEvent(layer, clicked)}
          field_edit={(irrig_unit_id, clicked) =>
            fieldEditEvent(irrig_unit_id, clicked)
          }
          fieldEditEvent={() => this}
          fieldClicked={fieldClicked}
          selectedField={selectedField}
          expand={expand}
          removeWells={removeWells}
          expandAll={expandAll}
          collapseAll={collapseAll}
          yearAcresDetails={yearAcresDetails}
          showWdpPanel={showWdpPanel}
          setShowWdpPanel={setShowWdpPanel}
          panelRef={panelRef}
        />
      )}
    </div>
  );
}

export default WDPMap;
